import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "@/lib/api";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Check, ChevronDown, LayoutGrid, Rows3, SearchX, SlidersHorizontal, X } from "lucide-react";
import { AnnouncementBar } from "@/components/home/AnnouncementBar";
import { Navbar } from "@/components/home/Navbar";
import { Footer } from "@/components/home/Footer";
import { FilterSidebar, type Filters } from "@/components/shop/FilterSidebar";
import { ProductCard } from "@/components/shop/ProductCard";
import { ProductCardSkeleton } from "@/components/shop/ProductCardSkeleton";
import { QuickView } from "@/components/shop/QuickView";
import { products, categories, colors } from "@/lib/products";
import { useWishlist } from "@/lib/store";

const categoryEnum = z.enum(categories);
const colorEnum = z.enum(colors.map((c) => c.key) as [string, ...string[]]);

const shopSearchSchema = z.object({
  category: categoryEnum.optional().catch(undefined),
  color: colorEnum.optional().catch(undefined),
  collection: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/shop/")({
  validateSearch: zodValidator(shopSearchSchema),
  head: () => ({
    meta: [
      { title: "Shop All Candles — Royal Lumina" },
      { name: "description", content: "Browse the complete Royal Lumina collection of handcrafted scented candles — luxury, artisan, decorative, aroma and gift sets." },
      { property: "og:title", content: "Shop All Candles — Royal Lumina" },
      { property: "og:description", content: "Handcrafted luxury scented candles for every moment." },
    ],
  }),
  component: ShopPage,
});

type Sort = "newest" | "popular" | "price-asc" | "price-desc" | "rating";

function SortDropdown({ sort, setSort }: { sort: Sort; setSort: (s: Sort) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const options: { value: Sort; label: string }[] = [
    { value: "newest", label: "Newest" },
    { value: "popular", label: "Most Popular" },
    { value: "price-asc", label: "Price: Low to High" },
    { value: "price-desc", label: "Price: High to Low" },
    { value: "rating", label: "Top Rated" },
  ];

  const currentLabel = options.find((o) => o.value === sort)?.label || "Sort products";

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-10 w-[155px] sm:w-[175px] items-center justify-between rounded-[12px] border border-border bg-card px-3.5 text-xs font-semibold tracking-tight text-primary shadow-xs transition-all duration-200 hover:border-gold/60 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-gold/30 cursor-pointer"
      >
        <span className="truncate">{currentLabel}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180 text-gold" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 min-w-[175px] rounded-[14px] border border-border/80 bg-card p-1.5 shadow-luxe backdrop-blur-md animate-in fade-in-0 zoom-in-95 duration-150">
          {options.map((opt) => {
            const isSelected = sort === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setSort(opt.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between rounded-[8px] px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                  isSelected
                    ? "bg-gold/15 text-gold font-semibold"
                    : "text-primary hover:bg-gold/10 hover:text-primary"
                }`}
              >
                <span>{opt.label}</span>
                {isSelected && <Check className="h-3.5 w-3.5 text-gold" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const defaultFilters: Filters & { collection: string | null } = {
  category: null,
  price: 2000,
  fragrances: [],
  color: null,
  inStockOnly: false,
  collection: null,
};

const INITIAL_BATCH = 12;
const BATCH_SIZE = 8;

function ShopPage() {
  const search = Route.useSearch();
  const [filters, setFilters] = useState<Filters & { collection: string | null }>({
    ...defaultFilters,
    category: search.category ?? null,
    color: search.color ?? null,
    collection: search.collection ?? null,
  });

  const { data: dbProducts = products } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/products`);
        if (res.ok) return await res.json();
      } catch (err) {
        // silent fallback
      }
      return products;
    }
  });

  // Keep filters in sync when the URL search params change (e.g. clicking
  // another collection card while already on /shop).
  useEffect(() => {
    setFilters((f) => ({
      ...f,
      category: search.category ?? null,
      color: search.color ?? null,
      collection: search.collection ?? null,
    }));
  }, [search.category, search.color, search.collection]);

  const [sort, setSort] = useState<Sort>("newest");
  const [grid, setGrid] = useState<"3" | "4">("3");
  const { has: isWished, toggle: toggleWish } = useWishlist();
  const [quickView, setQuickView] = useState<string | null>(null);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const [quickViewError, setQuickViewError] = useState<string | null>(null);
  const [mobileFilters, setMobileFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_BATCH);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const gridTopRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const activeCollectionName = useMemo(() => {
    if (!filters.collection) return null;
    const found = dbProducts.find((p: any) => p.collectionRef?.slug === filters.collection);
    if (found?.collectionRef?.name) return found.collectionRef.name;
    return filters.collection.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  }, [filters.collection, dbProducts]);

  const filtered = useMemo(() => {
    let list = dbProducts.filter((p: any) => {
      if (filters.category && p.category !== filters.category) return false;
      if (p.price > filters.price) return false;
      if (filters.fragrances.length && !filters.fragrances.includes(p.fragrance)) return false;
      if (filters.color && p.color !== filters.color) return false;
      if (filters.inStockOnly && !p.inStock) return false;
      if (filters.collection && (!p.collectionRef || p.collectionRef.slug !== filters.collection)) return false;
      return true;
    });
    switch (sort) {
      case "newest": list = [...list].sort((a, b) => b.newest - a.newest); break;
      case "popular": list = [...list].sort((a, b) => b.popularity - a.popularity); break;
      case "price-asc": list = [...list].sort((a, b) => a.price - b.price); break;
      case "price-desc": list = [...list].sort((a, b) => b.price - a.price); break;
      case "rating": list = [...list].sort((a, b) => b.rating - a.rating); break;
    }
    return list;
  }, [dbProducts, filters, sort]);

  // Simulated loading whenever filters/sort change
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(t);
  }, [filters, sort]);

  // Reset visible items count whenever filters or sort change.
  useEffect(() => {
    setVisibleCount(INITIAL_BATCH);
  }, [filters, sort]);

  const displayedItems = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);

  // Infinite scroll intersection observer with loading feedback state
  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleCount < filtered.length && !isFetchingMore) {
          setIsFetchingMore(true);
          setTimeout(() => {
            setVisibleCount((prev) => Math.min(prev + BATCH_SIZE, filtered.length));
            setIsFetchingMore(false);
          }, 300);
        }
      },
      { rootMargin: "250px" }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [visibleCount, filtered.length, isFetchingMore]);

  const activeChips = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (filters.collection) chips.push({ key: "collection", label: `Collection: ${activeCollectionName}`, onRemove: () => patch({ collection: null }) });
    if (filters.category) chips.push({ key: "cat", label: filters.category, onRemove: () => patch({ category: null }) });
    if (filters.color) chips.push({ key: "color", label: `Color: ${filters.color}`, onRemove: () => patch({ color: null }) });
    if (filters.inStockOnly) chips.push({ key: "stock", label: "In stock only", onRemove: () => patch({ inStockOnly: false }) });
    filters.fragrances.forEach((f) =>
      chips.push({ key: `f-${f}`, label: f, onRemove: () => patch({ fragrances: filters.fragrances.filter((x) => x !== f) }) }),
    );
    if (filters.price < 2000) chips.push({ key: "price", label: `Under ₹${filters.price}`, onRemove: () => patch({ price: 2000 }) });
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeCollectionName]);

  const activeProduct = dbProducts.find((p: any) => p.id === quickView) ?? null;

  // Simulated fetch for the quick view so loading / error states get airtime.
  const openQuickView = (id: string) => {
    setQuickView(id);
    loadQuickView(id);
  };
  const loadQuickView = (id: string) => {
    setQuickViewError(null);
    setQuickViewLoading(true);
    const t = setTimeout(() => {
      // Deterministically fail for a specific id so the error state is discoverable,
      // otherwise resolve after the shimmer.
      if (id === "__force_error__") {
        setQuickViewError("The connection flickered. Please try again.");
      }
      setQuickViewLoading(false);
    }, 700);
    return () => clearTimeout(t);
  };

  const patch = (p: Partial<Filters & { collection: string | null }>) => setFilters((f) => ({ ...f, ...p }));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AnnouncementBar />
      <Navbar />

      {/* Page hero */}
      <section className="relative bg-primary text-cream -mt-20 pt-32 pb-14">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_30%,var(--gold)_0%,transparent_45%)]" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="text-xs tracking-[0.3em] uppercase text-gold/80">
            Home · Shop{filters.collection ? ` · ${activeCollectionName}` : filters.category ? ` · ${filters.category}` : ""}
          </div>
          <h1 className="mt-4 font-display text-5xl md:text-6xl">
            {filters.collection ? (
              <>The <span className="italic text-gold">{activeCollectionName}</span></>
            ) : filters.category ? (
              <>The <span className="italic text-gold">{filters.category}</span> Collection</>
            ) : filters.color ? (
              <>The <span className="italic text-gold capitalize">{filters.color}</span> Edit</>
            ) : (
              <>The <span className="italic text-gold">Full Collection</span></>
            )}
          </h1>
          <p className="mt-4 max-w-xl text-cream/70">
            {filtered.length} handcrafted candles, curated for warmth, ritual and quiet luxury.
          </p>
        </div>
      </section>

      <main className="mx-auto max-w-[1400px] px-6 lg:px-10 py-14 pb-28 lg:py-20 lg:pb-20">
        <div className="grid lg:grid-cols-[280px_1fr] gap-10 lg:gap-14">
          <div className="hidden lg:block">
            <FilterSidebar filters={filters} onChange={patch} onReset={() => setFilters(defaultFilters)} />
          </div>

          <div>
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-border">
              {/* Mobile controls: Filters & Sort side by side */}
              <div className="flex items-center justify-between gap-3 w-full sm:w-auto">
                <button
                  onClick={() => setMobileFilters(true)}
                  className="lg:hidden inline-flex items-center gap-2 text-xs font-semibold px-3.5 h-10 border border-border bg-card rounded-[12px] hover:border-gold/60 transition-colors shadow-xs cursor-pointer"
                >
                  <SlidersHorizontal className="h-4 w-4 text-gold" /> Filters
                  {activeChips.length > 0 && (
                    <span className="ml-1 h-5 w-5 rounded-full bg-gold text-primary font-bold text-[10px] grid place-items-center">
                      {activeChips.length}
                    </span>
                  )}
                </button>

                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground lg:hidden">
                  <span>Sort</span>
                  <SortDropdown sort={sort} setSort={setSort} />
                </div>

                <div className="hidden lg:block text-sm text-muted-foreground">
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
                      Curating selection…
                    </span>
                  ) : (
                    <>
                      Showing <span className="text-primary font-medium">{displayedItems.length}</span> of {filtered.length}
                      {filtered.length !== dbProducts.length && (
                        <span className="text-muted-foreground/70"> · {dbProducts.length} total</span>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Mobile subtitle count & Desktop right controls */}
              <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto">
                <div className="lg:hidden text-xs text-muted-foreground">
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
                      Curating selection…
                    </span>
                  ) : (
                    <>
                      Showing <span className="text-primary font-medium">{displayedItems.length}</span> of {filtered.length}
                      {filtered.length !== dbProducts.length && (
                        <span className="text-muted-foreground/70"> · {dbProducts.length} total</span>
                      )}
                    </>
                  )}
                </div>

                <div className="hidden lg:flex items-center gap-3">
                  <div className="hidden md:flex items-center gap-1 border border-border rounded-[12px] p-1">
                    <button onClick={() => setGrid("3")} aria-label="3 columns" className={`h-8 w-8 grid place-items-center rounded-[8px] ${grid === "3" ? "bg-primary text-cream" : "text-muted-foreground hover:text-primary"}`}>
                      <LayoutGrid className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => setGrid("4")} aria-label="4 columns" className={`h-8 w-8 grid place-items-center rounded-[8px] ${grid === "4" ? "bg-primary text-cream" : "text-muted-foreground hover:text-primary"}`}>
                      <Rows3 className="h-3.5 w-3.5 rotate-90" />
                    </button>
                  </div>

                  <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                    <span>Sort</span>
                    <SortDropdown sort={sort} setSort={setSort} />
                  </div>
                </div>
              </div>
            </div>

            {/* Active filter chips */}
            {activeChips.length > 0 && !loading && (
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="text-[0.65rem] tracking-[0.25em] uppercase text-muted-foreground mr-1">Active</span>
                {activeChips.map((c) => (
                  <button
                    key={c.key}
                    onClick={c.onRemove}
                    className="group inline-flex items-center gap-1.5 h-8 pl-3 pr-2 rounded-full border border-border bg-card text-xs text-primary hover:border-gold transition-colors"
                  >
                    {c.label}
                    <X className="h-3 w-3 text-muted-foreground group-hover:text-gold transition-colors" />
                  </button>
                ))}
                <button
                  onClick={() => setFilters(defaultFilters)}
                  className="ml-1 text-[0.7rem] tracking-[0.2em] uppercase text-muted-foreground hover:text-gold transition-colors"
                >
                  Clear all
                </button>
              </div>
            )}

            <div ref={gridTopRef} className="scroll-mt-32" />

            {loading ? (
              <div className={`mt-8 grid gap-3 sm:gap-6 lg:gap-8 grid-cols-2 ${grid === "3" ? "lg:grid-cols-3" : "lg:grid-cols-3 xl:grid-cols-4"}`}>
                {Array.from({ length: INITIAL_BATCH }).map((_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState
                hasFilters={activeChips.length > 0}
                onReset={() => setFilters(defaultFilters)}
              />
            ) : (
              <div className={`mt-8 grid gap-3 sm:gap-6 lg:gap-8 grid-cols-2 ${grid === "3" ? "lg:grid-cols-3" : "lg:grid-cols-3 xl:grid-cols-4"}`}>
                {displayedItems.map((p: any) => (
                  <div key={p.id} className="animate-float-up">
                    <ProductCard
                      product={p}
                      wishlisted={isWished(p.id)}
                      onWishlist={() => toggleWish(p.id)}
                      onQuickView={() => openQuickView(p.id)}
                    />
                  </div>
                ))}
                {isFetchingMore && Array.from({ length: 4 }).map((_, i) => (
                  <ProductCardSkeleton key={`more-skel-${i}`} />
                ))}
              </div>
            )}

            {/* Infinite Scroll Trigger & Status */}
            {!loading && filtered.length > 0 && (
              <div ref={loadMoreRef} className="mt-12 py-6 text-center flex flex-col items-center justify-center">
                {visibleCount < filtered.length || isFetchingMore ? (
                  <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-gold/30 bg-card text-xs font-semibold text-gold shadow-md animate-pulse">
                    <span className="h-2 w-2 rounded-full bg-gold animate-ping" />
                    <span>Loading more items…</span>
                  </div>
                ) : (
                  <div className="text-[0.65rem] tracking-[0.3em] uppercase text-muted-foreground/70">
                    Showing all {filtered.length} handcrafted candles
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Mobile filter drawer */}
      {mobileFilters && (
        <div className="fixed inset-0 z-[100] lg:hidden">
          <div className="absolute inset-0 bg-primary/70 backdrop-blur-sm transition-opacity" onClick={() => setMobileFilters(false)} />
          <div className="absolute inset-y-0 left-0 w-[90%] max-w-sm bg-background shadow-2xl flex flex-col h-full z-10 animate-in slide-in-from-left duration-300">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-border bg-card">
              <div>
                <h3 className="font-display text-2xl font-semibold text-primary">Filters</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{filtered.length} products match</p>
              </div>
              <button
                onClick={() => setMobileFilters(false)}
                aria-label="Close filters"
                className="h-10 w-10 rounded-full border border-border bg-background grid place-items-center text-muted-foreground hover:text-primary hover:border-gold transition-colors cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Filter Content */}
            <div className="flex-1 overflow-y-auto p-5">
              <FilterSidebar filters={filters} onChange={patch} onReset={() => setFilters(defaultFilters)} />
            </div>

            {/* Sticky Bottom Actions */}
            <div className="p-4 border-t border-border bg-card/95 backdrop-blur-md sticky bottom-0 z-20 flex gap-3">
              <button
                onClick={() => setFilters(defaultFilters)}
                className="px-4 py-3 rounded-[12px] border border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-primary hover:border-gold/60 transition-colors cursor-pointer"
              >
                Reset
              </button>
              <button
                onClick={() => setMobileFilters(false)}
                className="flex-1 btn-gold justify-center h-11 text-xs font-semibold tracking-wider uppercase shadow-md cursor-pointer"
              >
                Apply ({filtered.length} items)
              </button>
            </div>
          </div>
        </div>
      )}

      <QuickView
        product={quickViewLoading || quickViewError ? null : activeProduct}
        loading={quickViewLoading}
        error={quickViewError}
        wishlisted={activeProduct ? isWished(activeProduct.id) : false}
        onWishlist={() => activeProduct && toggleWish(activeProduct.id)}
        onClose={() => {
          setQuickView(null);
          setQuickViewError(null);
          setQuickViewLoading(false);
        }}
        onRetry={() => quickView && loadQuickView(quickView)}
      />

      <Footer />
    </div>
  );
}

function EmptyState({ hasFilters, onReset }: { hasFilters: boolean; onReset: () => void }) {
  return (
    <div className="mt-8 py-20 lg:py-28 px-6 text-center bg-card rounded-[24px] border border-border/70 shadow-luxe">
      <div className="mx-auto h-16 w-16 rounded-full bg-secondary grid place-items-center">
        <SearchX className="h-7 w-7 text-gold" strokeWidth={1.4} />
      </div>
      <h3 className="mt-6 font-display text-3xl md:text-4xl">
        {hasFilters ? (
          <>No candles match <span className="italic text-gold">that ritual</span></>
        ) : (
          <>The collection is <span className="italic text-gold">resting</span></>
        )}
      </h3>
      <p className="mt-3 text-muted-foreground max-w-md mx-auto">
        {hasFilters
          ? "Try relaxing a fragrance, price or colour filter — your next signature scent is waiting."
          : "New pieces are being poured. Please check back shortly for the full collection."}
      </p>
      {hasFilters && (
        <button onClick={onReset} className="mt-8 btn-outline-dark">
          Reset all filters
        </button>
      )}
    </div>
  );
}

function getPageNumbers(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("…");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push("…");
  pages.push(total);
  return pages;
}
